import { Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { api, podatki, type Shema } from '../api/odjemalec';
import { datum } from '../oblika/oblika';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { barve } from '../tema';
import { jakost, zadnjihDni } from './skrbnistvo';
import { glave, obNapaki } from './zeton';

type Instanca = Shema['InstancaSkrbnik'];
type Dan = Shema['AktivnostDneva'];

const PROSOJNOST = [0, 0.3, 0.6, 1];

/**
 * Aktivnost uporabnikov instance v zadnjih 30 dneh (skrbništvo): za vsakega uporabnika pas 30 dni, temnejši dan = več
 * zahtev; ob dnevu število zahtev in odjemalci (splet, swing z verzijo). Prijava skrbnika kot uporabnik se ne šteje.
 */
export function AktivnostInstance({ i }: { i: Instanca }) {
  const aktivnost = useQuery({
    queryKey: ['skrbnik', 'aktivnost', i.companyId],
    queryFn: async ({ signal }) => {
      try {
        return await podatki(
          api.GET('/api/skrbnik/instance/{companyId}/aktivnost', {
            signal,
            headers: glave(),
            params: { path: { companyId: i.companyId! } },
          }),
        );
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const dnevi = zadnjihDni(30);
  const poUporabnikih = new Map<string, Map<string, Dan>>();
  for (const d of aktivnost.data ?? []) {
    const u = d.uporabniskoIme ?? '';
    if (!poUporabnikih.has(u)) {
      poUporabnikih.set(u, new Map());
    }
    poUporabnikih.get(u)!.set(d.dan ?? '', d);
  }

  return (
    <Kartica naslov="Aktivnost (zadnjih 30 dni)">
      {!aktivnost.data ? (
        <StanjeNalaganja
          napaka={aktivnost.error}
          ponovi={() => void aktivnost.refetch()}
          naslov="Aktivnosti ni bilo mogoče naložiti"
        />
      ) : poUporabnikih.size === 0 ? (
        <Text size="sm" c="dimmed">
          V zadnjih 30 dneh ni bilo aktivnosti.
        </Text>
      ) : (
        <Stack gap="sm">
          {[...poUporabnikih.entries()].map(([uporabnik, poDnevih]) => {
            const dni = poDnevih.size;
            const zahtev = [...poDnevih.values()].reduce((s, d) => s + (d.zahtev ?? 0), 0);
            return (
              <Box key={uporabnik}>
                <Group justify="space-between" gap="xs" mb={4}>
                  <Text size="sm" fw={600}>
                    {uporabnik}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {dni} {dni === 1 ? 'dan' : dni === 2 ? 'dneva' : dni < 5 ? 'dnevi' : 'dni'} · {zahtev} zahtev
                  </Text>
                </Group>
                <Box
                  aria-label={`Aktivnost ${uporabnik}`}
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(30, 1fr)', gap: 3 }}
                >
                  {dnevi.map((dan) => {
                    const d = poDnevih.get(dan);
                    const j = jakost(d?.zahtev ?? 0);
                    return (
                      <Tooltip
                        key={dan}
                        label={
                          d ? `${datum(dan)}: ${d.zahtev} zahtev (${d.odjemalci})` : `${datum(dan)}: brez aktivnosti`
                        }
                        withArrow
                      >
                        <Box
                          h={14}
                          style={{
                            borderRadius: 3,
                            background: j === 0 ? barve.obroba : barve.primarna,
                            opacity: j === 0 ? 1 : PROSOJNOST[j],
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Kartica>
  );
}
